import React from 'react'
import StepCrToggle from './steps/StepCrToggle'
import StepBulkApprove from './steps/StepBulkApprove'
import StepRevenueReset from './steps/StepRevenueReset'
import StepCrCleaner from './steps/StepCrCleaner'
import StepSetSuperCutoff from './steps/StepSetSuperCutoff'
import StepProgress from './steps/StepProgress'
import StepErrors from './steps/StepErrors'

/**
 * Mappa dichiarativa stepId -> funzione di render del contenuto.
 * Ogni voce riceve (data, handlers, loadings) e restituisce un ReactNode.
 * Aggiungere qui nuovi step senza modificare il componente SuperCutoffWizard.
 *
 * @type {Object.<string, function(Object, Object, Object): React.ReactNode>}
 */
export const STEP_CONTENT_MAP = {
  cr_block: (data, handlers, loadings) => (
    <StepCrToggle
      mode="block"
      crAvailable={data.crAvailable}
      onExecute={handlers.onExecute}
      loading={loadings.crToggle}
      loadingStatus={loadings.crStatus}
      result={data.result}
    />
  ),
  bulk_approve: (data, handlers, loadings) => (
    <StepBulkApprove
      preview={data.preview}
      result={data.result}
      nextPeriod={data.nextPeriod}
      loadingNextPeriod={loadings.cutoffPm}
      onPreview={handlers.onPreview}
      onExecute={handlers.onExecute}
      loadingPreview={loadings.bulkApprovePreview}
      loadingExecute={loadings.bulkApproveExecute}
    />
  ),
  revenue_reset: (data, handlers, loadings) => (
    <StepRevenueReset
      preview={data.preview}
      result={data.result}
      nextPeriod={data.nextPeriod}
      loadingNextPeriod={loadings.cutoffPm}
      onPreview={handlers.onPreview}
      onExecute={handlers.onExecute}
      loadingPreview={loadings.revenueResetPreview}
      loadingExecute={loadings.revenueResetExecute}
    />
  ),
  cr_cleaner: (data, handlers, loadings) => (
    <StepCrCleaner
      openCrs={data.openCrs}
      result={data.result}
      onLoadCrs={handlers.onLoadCrs}
      onExecute={handlers.onExecute}
      onSkip={handlers.onSkip}
      loadingCrs={loadings.crOpen}
      loadingExecute={loadings.crClean}
    />
  ),
  set_supercutoff: (data, handlers, loadings) => (
    <StepSetSuperCutoff
      result={data.result}
      nextPeriod={data.nextPeriod}
      loadingNextPeriod={loadings.cutoffPm}
      onExecute={handlers.onExecute}
      loading={loadings.setSuperCutoff}
    />
  ),
  progress: (data, handlers, loadings) => (
    <StepProgress
      progress={data.progress}
      lastUpdated={data.lastUpdated}
      onRefresh={handlers.onRefresh}
      loading={loadings.progress}
    />
  ),
  errors: (data, handlers, loadings) => (
    <StepErrors
      errors={data.errors}
      retryResult={data.retryResult}
      onLoadErrors={handlers.onLoadErrors}
      onRetry={handlers.onRetry}
      onComplete={handlers.onComplete}
      loadingErrors={loadings.errorsGet}
      loadingRetry={loadings.errorsRetry}
      canHandleErrors={data.canHandleErrors}
    />
  ),
  cr_unblock: (data, handlers, loadings) => (
    <StepCrToggle
      mode="unblock"
      crAvailable={data.crAvailable}
      onExecute={handlers.onExecute}
      loading={loadings.crToggle}
      loadingStatus={loadings.crStatus}
      result={data.result}
    />
  ),
}

/**
 * Restituisce il contenuto React per lo step indicato.
 * Emette un warning in development se lo stepId non e' riconosciuto.
 *
 * I parametri data e handlers devono essere oggetti non-null.
 * Se il chiamante non dispone di dati per lo step, passare {} come fallback.
 * Valori null vengono normalizzati internamente a {} per sicurezza.
 *
 * @param {string} stepId   - ID dello step da renderizzare
 * @param {Object} data     - Dati dello step (da stepData[stepId]), non null
 * @param {Object} handlers - Callback dello step (da handlers[stepId]), non null
 * @param {Object} loadings - Stato loading globale
 * @returns {React.ReactNode|null}
 */
export const renderStepContent = (stepId, data, handlers, loadings) => {
  const renderFn = STEP_CONTENT_MAP[stepId]

  if (!renderFn) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'SuperCutoffWizard: stepId non riconosciuto "' + stepId + '". ' +
        'Valori attesi: ' + Object.keys(STEP_CONTENT_MAP).join(', ') + '.'
      )
    }
    return null
  }

  // Normalizzazione difensiva: garantisce che data e handlers siano sempre oggetti
  const safeData = (data !== null && data !== undefined) ? data : {}
  const safeHandlers = (handlers !== null && handlers !== undefined) ? handlers : {}

  return renderFn(safeData, safeHandlers, loadings)
}

// ID dello step "Set SuperCutoff PM": una volta completato, il super cutoff e'
// stato registrato e il job di closure e' stato triggherato. Da quel momento
// gli step precedenti (e questo stesso) non hanno piu' senso da rivisitare:
// la sidebar li locka per evitare azioni confuse o regressioni di flusso.
// Il lock si auto-resetta quando il BE chiude la run (execution_id = null,
// tutti gli step tornano pending).
const POINT_OF_NO_RETURN_STEP_ID = 'set_supercutoff'

/**
 * Determina se uno step e' selezionabile dalla sidebar.
 * Uno step e' selezionabile se e' gia' completato, se e' in corso (in_progress)
 * o se e' lo step attivo corrente. La condizione in_progress e' necessaria per
 * permettere di tornare allo step corrente del processo dopo aver navigato
 * indietro a uno step gia' completato (coerente con la action SELECT_STEP del reducer).
 *
 * Guard "punto di non ritorno": una volta che `set_supercutoff` e' completed,
 * gli step fino a `set_supercutoff` incluso non sono piu' navigabili anche se
 * completed. Lo step attivo resta selezionabile per non perderne il focus.
 * Il parametro `steps` e' opzionale per retrocompatibilita': se non fornito,
 * la guard non viene applicata.
 *
 * @param {Object} step         - Oggetto step { id, title, status }. Se null o undefined, restituisce false.
 * @param {string} activeStepId - ID dello step attualmente attivo
 * @param {Array}  [steps]      - Lista completa degli step del wizard, usata per la guard "punto di non ritorno"
 * @returns {boolean}
 */
export const isStepSelectable = (step, activeStepId, steps) => {
  if (!step) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('isStepSelectable: parametro step e\' null o undefined.')
    }
    return false
  }

  // Guard "punto di non ritorno": blocca gli step fino a `set_supercutoff` incluso
  // quando quest'ultimo e' completed. Lo step attivo viene escluso dal lock.
  if (Array.isArray(steps) && step.id !== activeStepId) {
    const triggerIndex = steps.findIndex(function(s) { return s.id === POINT_OF_NO_RETURN_STEP_ID })
    if (triggerIndex !== -1 && steps[triggerIndex].status === 'completed') {
      const stepIndex = steps.findIndex(function(s) { return s.id === step.id })
      if (stepIndex !== -1 && stepIndex <= triggerIndex) {
        return false
      }
    }
  }

  return step.status === 'completed' || step.status === 'in_progress' || step.id === activeStepId
}
